import { ArrowLeftOutlined, CopyOutlined } from '@ant-design/icons';
import { Button, Spin, Tooltip, message } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { getSkill } from '../../services/skillsApi';
import { SEO_DEFAULTS } from '../../utils/seoConfig';
import CellCogMarkdown from '../Common/CellCogMarkdown';
import RichCodeBlock from '../Common/RichCodeBlock';
import SkillInstallCard from './SkillInstallCard';
import { LEGACY_SKILL_SLUGS, formatSkillDisplayName } from './skillsConfig';
import './SkillsPage.css';

const SkillDetailPage = () => {
  const { skillSlug } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [skill, setSkill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Redirect legacy pre-migration slugs (e.g. /skills/image-cog) to current URLs
  // so old links and canonical/OG tags always point at the new slug.
  useEffect(() => {
    const currentSlug = LEGACY_SKILL_SLUGS[skillSlug];
    if (currentSlug) {
      navigate(`/skills/${currentSlug}`, { replace: true });
    }
  }, [skillSlug, navigate]);

  useEffect(() => {
    const fetchSkill = async () => {
      if (!skillSlug) return;
      if (LEGACY_SKILL_SLUGS[skillSlug]) return; // redirecting — skip fetch
      try {
        setLoading(true);
        setError(null);
        const response = await getSkill(skillSlug);
        setSkill(response.data);
      } catch (err) {
        console.error('Failed to fetch skill:', err);
        if (err.response?.status === 404) {
          setError('Skill not found');
        } else {
          setError('Failed to load skill');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchSkill();
  }, [skillSlug]);

  const handleBack = () => {
    navigate('/skills');
  };

  const handleCopyMarkdown = async () => {
    if (!skill?.content) return;
    try {
      await navigator.clipboard.writeText(skill.content);
      message.success('Markdown copied to clipboard');
    } catch {
      message.error('Failed to copy');
    }
  };

  // Custom link renderer: external links open in new tab. Passed through
  // as extraComponents to CellCogMarkdown so the default CellCogMarkdown
  // link renderer is replaced for this surface.
  const linkRenderer = useCallback(({ href, children, ...props }) => {
    return <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
  }, []);

  const markdownExtras = useMemo(() => ({ a: linkRenderer }), [linkRenderer]);

  if (loading) {
    return (
      <div className={`skills-page ${isDarkMode ? 'dark' : 'light'}`}>
        <div className="skills-loading">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (error || !skill) {
    return (
      <div className={`skills-page ${isDarkMode ? 'dark' : 'light'}`}>
        <div className="skills-detail-toolbar">
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBack}>
            All Skills
          </Button>
        </div>
        <div className="skills-error">
          <p>{error || 'Skill not found'}</p>
        </div>
      </div>
    );
  }

  const { metadata, content } = skill;
  const displayName = formatSkillDisplayName(metadata.name);
  const seoTitle = `${metadata.emoji} ${displayName} - CellCog OpenClaw Skill`;
  const seoDescription = metadata.description;

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={`${SEO_DEFAULTS.url}/skills/${skillSlug}`} />
        {/* Out of SEO scope (Nitish, Sep 3 2026; CEL-2148): noindex for search engines; the
            markdown alternate is generated per skill by scripts/copySkillsDocs.js at prebuild. */}
        <meta name="robots" content="noindex, follow" />
        <link rel="alternate" type="text/markdown" href={`${SEO_DEFAULTS.url}/skills/${skillSlug}/index.md`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={`${SEO_DEFAULTS.url}/skills/${skillSlug}`} />
        <meta property="og:image" content={SEO_DEFAULTS.image} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
      </Helmet>

      <div className={`skills-page ${isDarkMode ? 'dark' : 'light'}`}>
        {/* Toolbar */}
        <div className="skills-detail-toolbar">
          <div className="skills-detail-toolbar-inner">
            <div className="skills-detail-toolbar-left">
              <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBack}>
                All Skills
              </Button>
              <span className="skills-detail-breadcrumb">
                <span className="skills-detail-breadcrumb-emoji">{metadata.emoji}</span>
                <span className="skills-detail-breadcrumb-name">{displayName}</span>
              </span>
            </div>
            <Tooltip title="Copy raw markdown">
              <Button type="text" icon={<CopyOutlined />} onClick={handleCopyMarkdown} size="small">
                Copy
              </Button>
            </Tooltip>
          </div>
        </div>

        {/* Content */}
        <div className="skills-detail-scroll">
          {/* Metadata header */}
          <div className="skills-detail-header">
            <span className="skills-detail-emoji">{metadata.emoji}</span>
            <h1 className="skills-detail-title">{displayName}</h1>
            <p className="skills-detail-description">{metadata.description}</p>
            <div className="skills-detail-badges">
              {metadata.os && metadata.os.length > 0 && (
                <div className="skills-detail-badge">
                  {metadata.os.map(os => (
                    <span key={os} className="skills-os-tag">{os}</span>
                  ))}
                </div>
              )}
              {metadata.dependencies && metadata.dependencies.length > 0 && (
                <div className="skills-detail-badge">
                  <span className="skills-dep-label">Requires:</span>
                  {metadata.dependencies.map(dep => (
                    <span
                      key={dep}
                      className="skills-dep-tag"
                      onClick={() => navigate(`/skills/${dep}`)}
                      role="link"
                      tabIndex={0}
                    >
                      {dep}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <SkillInstallCard skillName={metadata.name} />
          </div>

          {/* Markdown body */}
          <div className="skills-detail-content">
            <CellCogMarkdown
              content={content}
              isDarkMode={isDarkMode}
              enableCellCogTags="none"
              codeBlockComponent={RichCodeBlock}
              richCodeDefaultMode="raw"
              extraComponents={markdownExtras}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default SkillDetailPage;
